import { z } from "zod";
import { fetchApi } from "@/lib/api-helpers";
import {
  getVgsEnvironment,
  isAppInStagingEnvironment,
  validateVgsConfig,
} from "@/lib/vgs-utils";
import axios from "axios";
import { fetchVaultCredentials } from "../vault.api";
import { NextRequest } from "next/server";

// ========== SCHEMAS ==========

const applePaySessionResponseSchema = z.object({
  apple_pay: z.string().or(z.record(z.string(), z.unknown())).optional(),
  body: z.string().optional(),
});

const applePayDomainVerificationSchema = z.object({
  domain_verification_file: z.string().optional(),
});

// ========== TYPES ==========

export type ApplePaySessionResponse = z.infer<
  typeof applePaySessionResponseSchema
>;

export interface ApplePayHeader {
  publicKeyHash: string;
  ephemeralPublicKey: string;
  transactionId: string;
}

export interface ApplePayPaymentData {
  data: string;
  signature: string;
  version: string;
  header: ApplePayHeader;
}

export interface ApplePayToken {
  paymentData: ApplePayPaymentData;
}

export interface ApplePayPayload {
  token: ApplePayToken;
}

export interface PaymentRequestResponse {
  details?: Record<string, unknown>;
  token?: ApplePayToken | string | Record<string, unknown>;
  paymentData?: ApplePayPaymentData;
  paymentMethod?: Record<string, unknown>;
  transactionId?: string;
  requestId?: string;
  payerEmail?: string;
  payerName?: string;
  payerPhone?: string;
  shippingContact?: ApplePayJS.ApplePayPaymentContact | Record<string, unknown>;
  billingContact?: ApplePayJS.ApplePayPaymentContact | Record<string, unknown>;
}

export interface PaymentMethod {
  id: number;
  type: string;
  name: string;
  test: boolean;
  apple_pay?: boolean;
  public_api_key?: string;
  supported_brands?: string[];
  sandbox?: boolean;
  token_provider?: string;
  [key: string]: unknown;
}

export interface Cart {
  available_payment_methods?: PaymentMethod[];
  [key: string]: unknown;
}

// ========== TYPE GUARDS ==========

function hasPaymentData(obj: unknown): obj is { paymentData: unknown } {
  return (
    typeof obj === "object" &&
    obj !== null &&
    Object.prototype.hasOwnProperty.call(obj, "paymentData")
  );
}

function hasTransactionId(obj: unknown): obj is { transactionId: unknown } {
  return (
    typeof obj === "object" &&
    obj !== null &&
    Object.prototype.hasOwnProperty.call(obj, "transactionId")
  );
}

// ========== APPLE PAY VERIFICATION ==========

export async function fetchApplePayVerificationFile(request: NextRequest) {
  try {
    const forwardedHost = request.headers.get("x-forwarded-host");
    const hostHeader = request.headers.get("host");
    const hostname =
      forwardedHost || (hostHeader ? hostHeader.split(":")[0] : "");

    const data = await fetchApi({
      endpoint: `/public/v2025-06/apple_pay_domain/${hostname}`,
      method: "GET",
      outputSchema: applePayDomainVerificationSchema,
    });

    if (data && data.domain_verification_file) {
      return new Response(data.domain_verification_file, {
        headers: {
          "Content-Type": "text/plain",
        },
      });
    } else {
      console.error("No domain_verification_file found in response");
      return new Response("Error: Verification file not found in response", {
        status: 500,
        headers: {
          "Content-Type": "text/plain",
        },
      });
    }
  } catch (error) {
    console.error("Error in Apple Pay verification route:", error);
    return new Response("Server error", {
      status: 500,
      headers: {
        "Content-Type": "text/plain",
      },
    });
  }
}

// ========== APPLE PAY SESSION VALIDATION ==========

export async function validateApplePaySession(
  validationURL: string,
  cartToken: string,
  companyId?: number,
  countryId?: number,
): Promise<ApplePaySessionResponse> {
  const result = await fetchApi({
    endpoint: `/payments/apple_pay_session`,
    method: "POST",
    input: {
      apple_session_url: validationURL,
      cart_token: cartToken,
      company_id: companyId,
      country_id: countryId,
    },
    outputSchema: applePaySessionResponseSchema,
  });

  return result;
}

// ========== PAYLOAD PREPARATION ==========

export function prepareApplePayPaymentMethodPayload(
  response: PaymentRequestResponse,
): ApplePayPayload {
  const details = response.details || response;

  const pm = details.paymentMethod || response.paymentMethod || {};

  const transactionId =
    (hasTransactionId(details) &&
      typeof details.transactionId === "string" &&
      details.transactionId) ||
    (hasTransactionId(pm) &&
      typeof pm.transactionId === "string" &&
      pm.transactionId) ||
    (typeof response.requestId === "string" && response.requestId) ||
    (response.paymentData &&
      typeof response.paymentData.header?.transactionId === "string" &&
      response.paymentData.header.transactionId) ||
    `txn-${Date.now()}`;

  let paymentData: ApplePayPaymentData | null = null;

  if (details.paymentData) {
    paymentData = details.paymentData as ApplePayPaymentData;
  } else if (
    details.token &&
    typeof details.token === "object" &&
    hasPaymentData(details.token)
  ) {
    paymentData = details.token.paymentData as ApplePayPaymentData;
  } else if (
    response.token &&
    typeof response.token === "object" &&
    hasPaymentData(response.token)
  ) {
    paymentData = response.token.paymentData as ApplePayPaymentData;
  }

  const billingContact =
    (details as Record<string, unknown>).billingContact ||
    response.billingContact;
  const shippingContact =
    (details as Record<string, unknown>).shippingContact ||
    response.shippingContact;

  if (paymentData) {
    const safePaymentData: ApplePayPaymentData = {
      data:
        typeof paymentData.data === "string"
          ? paymentData.data
          : typeof paymentData.data === "object" && paymentData.data !== null
            ? JSON.stringify(paymentData.data)
            : "simulated-data",
      signature:
        typeof paymentData.signature === "string"
          ? paymentData.signature
          : "simulated-signature",
      version:
        typeof paymentData.version === "string" ? paymentData.version : "EC_v1",
      header: {
        publicKeyHash:
          typeof paymentData.header?.publicKeyHash === "string"
            ? paymentData.header.publicKeyHash
            : "simulated-hash",
        ephemeralPublicKey:
          typeof paymentData.header?.ephemeralPublicKey === "string"
            ? paymentData.header.ephemeralPublicKey
            : "simulated-key",
        transactionId:
          typeof paymentData.header?.transactionId === "string"
            ? paymentData.header.transactionId
            : transactionId,
      },
    };

    return {
      token: { paymentData: safePaymentData },
    };
  }

  if (details.token) {
    const tokenValue = details.token;
    const tokenData =
      typeof tokenValue === "string" ? tokenValue : JSON.stringify(tokenValue);

    return {
      token: {
        paymentData: {
          data: tokenData,
          signature: "web-payment-request",
          version: "EC_v1",
          header: {
            publicKeyHash: "web-payment-request",
            ephemeralPublicKey: "web-payment-request",
            transactionId,
          },
        },
      },
    };
  }

  return {
    token: {
      paymentData: {
        data: JSON.stringify({
          originalTransactionId: transactionId,
          requestId: response.requestId || `req-${Date.now()}`,
          timestamp: new Date().toISOString(),
          billingContact: billingContact || {},
          shippingContact: shippingContact || {},
          payerEmail: response.payerEmail || "",
          payerName: response.payerName || "",
          payerPhone: response.payerPhone || "",
        }),
        signature: "web-payment-request",
        version: "EC_v1",
        header: {
          publicKeyHash: "web-payment-request",
          ephemeralPublicKey: "web-payment-request",
          transactionId,
        },
      },
    },
  };
}

// ========== VGS ERROR HANDLING ==========

export function handleVgsApplePayError(error: unknown): string {
  // Check for Axios error with API response
  if (axios.isAxiosError(error) && error.response?.data?.error_message) {
    return error.response.data.error_message;
  }

  // Check for error_message in plain error object
  if (error && typeof error === "object" && "error_message" in error) {
    return (error as { error_message: string }).error_message;
  }

  // Standard Error object
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === "string") {
    return error;
  }

  return "Payment processing failed. Please try again.";
}

// ========== VGS APPLE PAY AUTHORIZATION ==========

export async function authorizeApplePayWithVgsProxy(
  cartToken: string,
  paymentResponse: PaymentRequestResponse,
  deviceData?: string,
  paymentType?: "paypal",
) {
  try {
    const vaultCredentials = await fetchVaultCredentials(cartToken);

    if (!validateVgsConfig(vaultCredentials.vault)) {
      throw new Error("Invalid VGS configuration for Apple Pay");
    }

    const { vault_id, environment } = vaultCredentials.vault;

    const vgsEnvironment = getVgsEnvironment(environment);

    const basePath = `api/carts/${cartToken}/payment_methods/apple_pay`;
    const apiPath = isAppInStagingEnvironment()
      ? `${basePath}?payment_from=vgs_cmp_staging_test`
      : basePath;

    const proxiedCardPaymentMethodUrl = new URL(
      apiPath,
      `https://${vault_id}.${vgsEnvironment}.verygoodproxy.com/`,
    ).toString();

    const sendPayloadData: Record<string, unknown> = {
      token: paymentResponse.details?.token,
    };
    if (deviceData) {
      sendPayloadData.device_data = deviceData;
    }
    // Decrypted PPCP Apple Pay: VGS decrypts the token, then backend forwards
    // the PAN + cryptogram to PayPal instead of registering with the card PSP.
    // Backend dispatches on payment_type inside CartPaymentMethods::CreateAction.
    if (paymentType) {
      sendPayloadData.payment_type = paymentType;
    }

    await axios.post(proxiedCardPaymentMethodUrl, sendPayloadData, {
      headers: {
        "Content-Type": "application/json",
      },
      validateStatus: (status) => status === 200 || status === 201,
    });

    return true;
  } catch (error) {
    console.error("VGS Apple Pay authorization failed:", error);
    const errorMessage = handleVgsApplePayError(error);
    throw new Error(errorMessage);
  }
}

// ========== VGS UTILITY FUNCTIONS ==========

export function shouldUseVgsForApplePay(cart: Cart): boolean {
  if (!cart?.available_payment_methods) {
    return false;
  }

  const hasVgsPaymentMethod = cart.available_payment_methods.some(
    (method: PaymentMethod) =>
      method.type === "credit_card" && method.token_provider === "vgs",
  );

  return hasVgsPaymentMethod;
}
